import {
  Drawer,
  Box,
  IconButton,
  Typography,
  Tabs,
  Tab,
  useMediaQuery,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";

/**
 * SideDrawer — Phase 2 (Design Gap Audit item B) reusable component.
 *
 * Matches the prototype's slide-over panel used for row detail (see
 * .drawer/.drawer-h/.drawer-b/.drawer-f and the openPosition() function in
 * the HTML prototype): a fixed-width panel sliding in from the right, with
 * a header (title/subtitle/meta + close button), an optional tab strip,
 * scrollable body content, and a footer action bar.
 *
 * This is a brand-new component — no reusable Drawer existed anywhere in
 * this codebase before this. It intentionally owns ONLY presentation
 * (open/close, tab switching UI, layout) — it does not fetch data, know
 * about any specific entity (Position/Candidate/Client), or call any API.
 * The calling page passes in whatever content it already has.
 *
 * Responsive behavior: on desktop this is a fixed-width right-hand panel
 * (480px, matching a comfortable reading width for the prototype's detail
 * content); on mobile (below MUI's `sm` breakpoint) it becomes a full-width
 * panel so it doesn't leave an unusably narrow content area, matching the
 * "mobile-friendly presentation" requirement.
 *
 * Props:
 *   open        - boolean, whether the drawer is visible (required)
 *   onClose     - called when the close button or backdrop is clicked (required)
 *   title       - main heading, e.g. a position's role name (required)
 *   subtitle    - optional secondary line under the title (e.g. "Company · SPOC · opened 5 days ago")
 *   meta        - optional element rendered above the title (e.g. status chips), matches
 *                 the prototype's chip row above the <h2>
 *   tabs        - optional array of tab labels, e.g. ["Pipeline", "Job description", "Activity", "Notes"]
 *   activeTab   - index of the currently selected tab (required if `tabs` is provided)
 *   onTabChange - (newIndex) => void (required if `tabs` is provided)
 *   children    - body content for the currently active tab (required)
 *   footer      - optional element rendered in the sticky footer action bar
 *                 (e.g. primary/secondary buttons) — omit for a drawer with no footer actions
 *   width       - desktop width in px (default 480, prototype-comfortable width)
 */
export default function SideDrawer({
  open,
  onClose,
  title,
  subtitle,
  meta,
  tabs,
  activeTab,
  onTabChange,
  children,
  footer,
  width = 480,
}) {
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("sm"));

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: isMobile ? "100%" : width,
          maxWidth: "100vw",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      {/* Header — matches .drawer-h */}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: 1,
          p: 2,
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {meta && (
            <Box
              sx={{
                display: "flex",
                gap: 0.75,
                alignItems: "center",
                mb: 0.75,
              }}
            >
              {meta}
            </Box>
          )}
          <Typography
            sx={{
              fontSize: 17,
              fontWeight: 650,
              letterSpacing: "-0.3px",
              lineHeight: 1.25,
            }}
          >
            {title}
          </Typography>
          {subtitle && (
            <Typography sx={{ fontSize: 13, color: "text.secondary", mt: 0.4 }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        <IconButton onClick={onClose} size="small" aria-label="Close">
          <CloseRoundedIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Tabs — matches .tabs */}
      {tabs && tabs.length > 0 && (
        <Tabs
          value={activeTab}
          onChange={(_, v) => onTabChange(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            minHeight: 40,
            borderBottom: "1px solid",
            borderColor: "divider",
            px: 1,
            "& .MuiTab-root": {
              minHeight: 40,
              textTransform: "none",
              fontSize: 13,
              fontWeight: 600,
            },
          }}
        >
          {tabs.map((label) => (
            <Tab key={label} label={label} />
          ))}
        </Tabs>
      )}

      {/* Body — matches .drawer-b, scrollable */}
      <Box sx={{ flex: 1, overflowY: "auto", p: 2 }}>{children}</Box>

      {/* Footer — matches .drawer-f, only rendered if actions are provided */}
      {footer && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            p: 1.5,
            borderTop: "1px solid",
            borderColor: "divider",
          }}
        >
          {footer}
        </Box>
      )}
    </Drawer>
  );
}
